export type LinkPopupPlacement = 'bottom' | 'auto';

export interface LinkPopupAnchorRect {
  top: number;
  bottom: number;
}

export interface LinkPopupPlacementInput {
  anchorHeight: number;
  anchorRect: LinkPopupAnchorRect;
  popupHeight: number;
  viewportHeight: number;
  anchorBorderTopWidth?: number;
  viewportMargin?: number;
  topGap?: number;
}

// 'fit'은 콘텐츠 높이를 그대로 노출하는 기본 배치, 'constrained'는 화면 안쪽으로 보정해
// 최대 높이를 제한하고 내부 스크롤을 적용하는 배치다. 두 변형을 판별 유니언으로 표현해
// maxHeight/overflowY가 짝으로만 존재하는 불변식을 타입 수준에서 보장한다.
export type LinkPopupPlacementStyle =
  | { kind: 'fit'; top: number }
  | { kind: 'constrained'; top: number; maxHeight: number; overflowY: 'auto' };

const DEFAULT_VIEWPORT_MARGIN = 12;
const DEFAULT_TOP_GAP = 1;

export function resolveLinkPopupAutoPlacement(input: LinkPopupPlacementInput): LinkPopupPlacementStyle {
  const viewportMargin = input.viewportMargin ?? DEFAULT_VIEWPORT_MARGIN;
  const topGap = input.topGap ?? DEFAULT_TOP_GAP;
  const anchorBorderTopWidth = input.anchorBorderTopWidth ?? 0;
  const viewportTop = viewportMargin;
  const viewportBottom = input.viewportHeight - viewportMargin;

  // 측정값이 음수/0이거나 viewportMargin이 viewportHeight를 잡아먹는 등 의미 있는 배치 계산이
  // 불가능한 입력에서는 기본 아래 배치로 안전하게 빠져나간다. 호출 측에서 popupHeight=0을
  // 미리 막아두지만, 공용 함수 단독 호출(예: 테스트, 향후 사용처)에서도 동일한 안전장치가 필요하다.
  if (input.popupHeight <= 0 || viewportBottom <= viewportTop) {
    console.warn(
      'resolveLinkPopupAutoPlacement: 비정상 입력 — 기본 아래 배치 사용',
      {popupHeight: input.popupHeight, viewportHeight: input.viewportHeight, viewportMargin},
    );
    return bottomPlacement(input.anchorHeight);
  }

  if (input.anchorRect.bottom + input.popupHeight <= viewportBottom) {
    return bottomPlacement(input.anchorHeight);
  }

  if (input.anchorRect.top - input.popupHeight - topGap >= viewportTop) {
    return topPlacement(input.popupHeight, topGap, anchorBorderTopWidth);
  }

  const availableAbove = Math.max(0, input.anchorRect.top - viewportTop);
  const availableBelow = Math.max(0, viewportBottom - input.anchorRect.bottom);
  if (availableAbove >= availableBelow) {
    // anchor border 두께만큼 host top이 안쪽으로 들어와 있으므로,
    // top 보정뿐 아니라 maxHeight 예산에서도 같은 값을 차감해야
    // 팝업 상단이 viewportTop을 침범하지 않는다.
    const maxHeight = Math.max(0, availableAbove - topGap - anchorBorderTopWidth);
    return {
      kind: 'constrained',
      top: -maxHeight - topGap - anchorBorderTopWidth,
      maxHeight,
      overflowY: 'auto',
    };
  }

  return {
    kind: 'constrained',
    top: input.anchorHeight,
    maxHeight: availableBelow,
    overflowY: 'auto',
  };
}

function bottomPlacement(anchorHeight: number): LinkPopupPlacementStyle {
  return {
    kind: 'fit',
    top: anchorHeight,
  };
}

function topPlacement(popupHeight: number, topGap: number, anchorBorderTopWidth: number): LinkPopupPlacementStyle {
  // host top은 anchor padding 안쪽 기준으로 측정되므로 anchor border 두께를 포함해
  // 보이는 팝업 가장자리가 보이는 anchor 가장자리에서 분리되도록 보정한다.
  return {
    kind: 'fit',
    top: -popupHeight - topGap - anchorBorderTopWidth,
  };
}
